<template>
  <span class="df-wrapper" v-if="isEnabled">
    <!-- Trigger button -->
    <button class="btn-filter-trigger" @click="openDrawer">
      <img :src="icoFilter" alt="filter" width="15" height="15" />
      <span v-if="activeCount > 0" class="df-badge">{{ activeCount }}</span>
    </button>

    <!-- Overlay -->
    <transition name="df-fade-overlay">
      <div v-if="drawerOpen" class="df-overlay" @click="closeDrawer"></div>
    </transition>

    <!-- Drawer panel — v-show keeps FilterBetweenDatesSelect mounted (like el-popover) -->
    <transition name="df-slide">
      <div v-show="drawerOpen" class="df-panel">
        <div class="df-panel__header">
          <span>Filtros</span>
          <button class="df-icon-btn" @click="closeDrawer">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <div class="df-panel__body" v-loading="loading">
          <!-- Dates -->
          <template v-if="isDates">
            <div class="df-section">
              <label class="df-label">
                Fechas
                <MoreInfoComponent
                  content="Por defecto se listan el mes actual"
                />
              </label>
              <FilterBetweenDatesSelect
                :keyRangeDates="localKeyRangeDates"
                @onAsyncDataForDates="onAsyncDataForDates"
              />
            </div>
          </template>

          <!-- Clients -->
          <template v-if="isClients">
            <div class="df-section">
              <label class="df-label">Empresa / cliente</label>
              <!-- :key usa clientResetKey (no localClientId) para evitar remount
                   en cada selección — solo se remonta al hacer reset de filtros -->
              <SelectBusinessComponent
                :key="clientResetKey"
                title=""
                :isAddBusiness="false"
                :hideEditButton="true"
                :setUserBusinessClientId="localClientId"
                @onChangeSelect="onChangeSelectBusiness"
              />
            </div>
          </template>

          <!-- Title -->
          <template v-if="isTitle">
            <div class="df-section">
              <label class="df-label">Título del documento</label>
              <div class="df-input-wrap">
                <input
                  v-model="localTitle"
                  type="text"
                  class="df-input"
                  placeholder="Buscar por título"
                />
                <i class="el-icon-search df-input__icon"></i>
              </div>
            </div>
          </template>

          <!-- Search -->
          <template v-if="isSearch">
            <div class="df-section">
              <label class="df-label">Búsqueda</label>
              <div class="df-input-wrap">
                <input
                  v-model="localSearch"
                  type="text"
                  class="df-input"
                  :placeholder="placeholder"
                />
                <i class="el-icon-search df-input__icon"></i>
              </div>
            </div>
          </template>

          <!-- Order by -->
          <template v-if="isOrderBy">
            <div class="df-section">
              <label class="df-label">Ordenar por</label>
              <div class="df-order-grid">
                <button
                  class="df-order-btn"
                  :class="{ active: localOrderBy === 'desc' }"
                  @click="localOrderBy = 'desc'"
                >
                  Mayor valor
                </button>
                <button
                  class="df-order-btn"
                  :class="{ active: localOrderBy === 'asc' }"
                  @click="localOrderBy = 'asc'"
                >
                  Menor valor
                </button>
              </div>
            </div>
          </template>

          <!-- Status -->
          <template v-if="isStatus">
            <div class="df-section">
              <label class="df-label">Filtrar por</label>
              <div class="df-radio-group">
                <label class="df-radio" v-for="s in setStatus" :key="s.key">
                  <input type="radio" v-model="localStatus" :value="s.key" />
                  {{ s.label }}
                </label>
              </div>
            </div>
          </template>

          <!-- Custom slot -->
          <slot name="customs"></slot>
        </div>

        <div class="df-panel__footer">
          <button class="df-clear-btn" @click="resetFilters">
            Limpiar filtros
          </button>
          <button class="df-apply-btn" @click="applyFilters">Buscar</button>
        </div>
      </div>
    </transition>
  </span>
</template>

<script>
import moment from "moment";
import FilterBetweenDatesSelect from "../../components/FilterBetweenDatesSelect";
import MoreInfoComponent from "../../components/MoreInfo";
import SelectBusinessComponent from "../../components/SelectBusinessV2";
import { icoFilter } from "@/services/resources";

export default {
  name: "DrawerFilterComponent",
  components: {
    FilterBetweenDatesSelect,
    MoreInfoComponent,
    SelectBusinessComponent
  },

  props: {
    setStatus: { type: Array },
    placeholder: { default: "Buscar" },
    isOrderBy: { default: false, type: Boolean },
    isSearch: { default: false, type: Boolean },
    isStatus: { default: false, type: Boolean },
    isDates: { default: false, type: Boolean },
    isClients: { default: false, type: Boolean },
    isTitle: { default: false, type: Boolean },
    loading: { default: false, type: Boolean },
    setFilters: { default: null, type: Object }
  },

  data() {
    return {
      icoFilter,
      drawerOpen: false,
      localSearch: null,
      localOrderBy: null,
      localStatus: null,
      localFrom: null,
      localTo: null,
      localKeyRangeDates: null,
      localClientId: null,
      localTitle: null,
      // Contador para forzar remount de SelectBusiness SOLO en reset,
      // no en cada cambio de clientId (evita API calls innecesarios)
      clientResetKey: 0
    };
  },

  computed: {
    isEnabled() {
      return (
        this.isOrderBy ||
        this.isSearch ||
        this.isStatus ||
        this.isDates ||
        this.isClients ||
        this.isTitle
      );
    },

    activeCount() {
      if (!this.setFilters) return 0;
      let n = 0;
      const {
        search,
        title,
        keyRangeDates,
        orderBy,
        status,
        clientId
      } = this.setFilters;
      if (search) n++;
      if (title) n++;
      // El mes en curso es la vista por defecto, no un filtro aplicado por el
      // usuario → solo cuenta si eligió otro rango distinto a "currentMonth".
      if (keyRangeDates && keyRangeDates !== "currentMonth") n++;
      if (orderBy) n++;
      if (status) n++;
      if (clientId) n++;
      return n;
    }
  },

  watch: {
    setFilters(val) {
      if (!val) return;
      // Use || null so empty strings from the Vuex store are also treated as no value
      const {
        search,
        orderBy,
        status,
        from,
        to,
        keyRangeDates,
        clientId,
        title
      } = val;
      this.localSearch = search || null;
      this.localOrderBy = orderBy || null;
      this.localStatus = status || null;
      this.localFrom = from || null;
      this.localTo = to || null;
      this.localKeyRangeDates = keyRangeDates || null;
      this.localClientId = clientId || null;
      this.localTitle = title || null;
    }
  },

  methods: {
    openDrawer() {
      // Sync local state from current applied filters
      if (this.setFilters) {
        const {
          search,
          orderBy,
          status,
          from,
          to,
          keyRangeDates,
          clientId,
          title
        } = this.setFilters;
        this.localSearch = search || null;
        this.localOrderBy = orderBy || null;
        this.localStatus = status || null;
        this.localFrom = from || null;
        this.localTo = to || null;
        this.localKeyRangeDates = keyRangeDates || null;
        this.localClientId = clientId || null;
        this.localTitle = title || null;
      }
      this.drawerOpen = true;
    },

    closeDrawer() {
      this.drawerOpen = false;
    },

    // Compute from/to live from the key so stale stored values never reach the API.
    // Custom date-range ("dateRages") uses localFrom/localTo from the date picker.
    resolveDates(key) {
      const today = moment().format("YYYY-MM-DD");
      switch (key) {
        case "currentDay":
          return { from: today, to: today };
        case "lastWeek":
          return {
            from: moment()
              .subtract(7, "days")
              .startOf("week")
              .format("YYYY-MM-DD"),
            to: today
          };
        case "currentMonth":
          return {
            from: moment()
              .startOf("month")
              .format("YYYY-MM-DD"),
            to: moment()
              .endOf("month")
              .format("YYYY-MM-DD")
          };
        case "lastMonth":
          return {
            from: moment()
              .subtract(1, "months")
              .startOf("month")
              .format("YYYY-MM-DD"),
            to: moment()
              .subtract(1, "months")
              .endOf("month")
              .format("YYYY-MM-DD")
          };
        case "currentYear":
          return {
            from: moment()
              .startOf("year")
              .format("YYYY-MM-DD"),
            to: moment()
              .endOf("year")
              .format("YYYY-MM-DD")
          };
        case "lastYear":
          return {
            from: moment()
              .subtract(1, "years")
              .startOf("year")
              .format("YYYY-MM-DD"),
            to: moment()
              .subtract(1, "years")
              .endOf("year")
              .format("YYYY-MM-DD")
          };
        case "dateRages":
          // Custom picker — use whatever localFrom/localTo the picker set
          return { from: this.localFrom, to: this.localTo };
        default:
          return { from: null, to: null };
      }
    },

    buildFilters() {
      // Use the dates that FilterBetweenDatesSelect computed and emitted,
      // NOT the dates from resolveDates() which would override them with different logic.
      // resolveDates is kept as fallback for clarity, but should not be called here.
      return {
        search: this.localSearch,
        orderBy: this.localOrderBy,
        status: this.localStatus,
        from: this.localFrom,
        to: this.localTo,
        keyRangeDates: this.localKeyRangeDates,
        clientId: this.localClientId,
        title: this.localTitle
      };
    },

    applyFilters() {
      this.$emit("onChangeFilters", this.buildFilters());
      this.$emit("onApplyFilters");
      this.closeDrawer();
    },

    resetFilters() {
      this.localSearch = null;
      this.localOrderBy = null;
      this.localStatus = null;
      this.localFrom = null;
      this.localTo = null;
      this.localKeyRangeDates = null;
      this.localClientId = null;
      this.localTitle = null;
      this.clientResetKey++; // fuerza remount de SelectBusiness → campo queda vacío
      // Orden correcto:
      // 1. Notifica el nuevo estado vacío (searchFilters queda limpio)
      this.$emit("onChangeFilters", this.buildFilters());
      // 2. Permite que componentes con filtros extra (ej. is_paid en InvoiceColumns)
      //    los reseteen ANTES de que se dispare el reload
      this.$emit("onResetFilters");
      // 3. Dispara la recarga de datos con los filtros ya limpios
      this.$emit("onApplyFilters");
      this.closeDrawer();
    },

    onAsyncDataForDates({ searchDates }) {
      const { from, to, keyRangeDates } = searchDates;
      this.localFrom = from;
      this.localTo = to;
      this.localKeyRangeDates = keyRangeDates;
      // NO emitir onChangeFilters aquí - esperar a que usuario haga click "Buscar"
    },

    onChangeSelectBusiness({ client }) {
      // Usar optional chaining por si el componente emite null al limpiar selección
      this.localClientId = client?.id ?? null;
    }
  }
};
</script>

<style lang="scss">
// All inputs inside the drawer panel: text dark, placeholder grey
.df-panel .el-input__inner,
.df-panel .el-autocomplete .el-input__inner,
.df-panel .df-section .has-value .el-input__inner {
  color: #2d2b55 !important;
  font-weight: 500;
}
.df-panel .el-input__inner::placeholder,
.df-panel .el-autocomplete .el-input__inner::placeholder {
  color: #c5c8d2 !important;
  font-weight: 400;
}

// el-date-picker inside drawer panel (not scoped — pierces component)
.df-panel .el-date-editor {
  width: 100% !important;
  .el-input__inner {
    border: none !important;
    border-bottom: 1.5px solid #e4eaf6 !important;
    border-radius: 0 !important;
    font-size: 13px !important;
    color: #2d2b55 !important;
    background: transparent !important;
    height: 36px !important;
    line-height: 34px !important;
    padding-left: 0 !important;
    padding-right: 28px !important;
    box-shadow: none !important;
    &:focus {
      border-bottom-color: #3ecfb4 !important;
    }
  }
  .el-input__prefix {
    display: none !important;
  }
  .el-input__suffix {
    color: #888;
  }
}
</style>

<style lang="scss" scoped>
$teal: #3ecfb4;
$dark: #2d2b55;
$grey: #888;
$border: #e8e7e3;

// ── Wrapper (inline so it doesn't break flex layouts) ────
.df-wrapper {
  display: inline-flex;
  align-items: center;
  position: relative;
}

// ── Trigger button ────────────────────────────────────────
.btn-filter-trigger {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #e4eaf6;
  background: #fff;
  cursor: pointer;
  padding: 0;
  transition: all 0.15s;
  img {
    opacity: 0.55;
    transition: opacity 0.15s;
  }
  &:hover {
    border-color: #c8c5d0;
    background: #f5f7fb;
    img {
      opacity: 0.8;
    }
  }
}

// ── Active filter badge ───────────────────────────────────
.df-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #e24b4a;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  min-width: 15px;
  height: 15px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
}

// ── Overlay ───────────────────────────────────────────────
.df-overlay {
  position: fixed;
  inset: 0;
  background: rgba(45, 43, 85, 0.25);
  z-index: 1040;
}

.df-fade-overlay-enter-active,
.df-fade-overlay-leave-active {
  transition: opacity 0.22s ease;
}
.df-fade-overlay-enter,
.df-fade-overlay-leave-to {
  opacity: 0;
}

// ── Drawer panel ─────────────────────────────────────────
.df-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 360px;
  background: #fff;
  border-left: 1px solid $border;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  box-shadow: -6px 0 24px rgba(45, 43, 85, 0.14);

  @media (max-width: 576px) {
    width: 100vw;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid $border;
    font-size: 14px;
    font-weight: 600;
    color: $dark;
    flex-shrink: 0;
    background: #fff;
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-top: 1px solid $border;
    flex-shrink: 0;
    background: #fff;
  }
}

// ── Slide transition ──────────────────────────────────────
.df-slide-enter-active,
.df-slide-leave-active {
  transition: transform 0.22s ease;
}
.df-slide-enter,
.df-slide-leave-to {
  transform: translateX(100%);
}

// When hidden via v-show, prevent panel from intercepting pointer events
.df-panel[style*="display: none"],
.df-panel[style*="display:none"] {
  pointer-events: none;
}

// ── Close button ─────────────────────────────────────────
.df-icon-btn {
  background: none;
  border: none;
  color: $grey;
  font-size: 17px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  transition: color 0.15s, background 0.15s;
  &:hover {
    color: $dark;
    background: #f0f2f7;
  }
}

// ── Sections ─────────────────────────────────────────────
.df-section {
  padding: 10px 0;
}

.df-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #a0a4b0;
  margin-bottom: 8px;
  letter-spacing: 0.2px;
}

// ── Input ─────────────────────────────────────────────────
.df-input-wrap {
  position: relative;
}

.df-input {
  width: 100%;
  padding: 8px 28px 8px 0;
  border: none;
  border-bottom: 1.5px solid #e4eaf6;
  border-radius: 0;
  font-size: 13px;
  color: #2d2b55;
  font-weight: 500;
  background: transparent;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
  &:focus {
    border-bottom-color: $teal;
  }
  &::placeholder {
    color: #c5c8d2;
    font-weight: 400;
  }
}

.df-input__icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: $grey;
  font-size: 14px;
  pointer-events: none;
}

// ── Order buttons ─────────────────────────────────────────
.df-order-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.df-order-btn {
  padding: 8px 6px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid $border;
  background: #fff;
  color: #5a5880;
  cursor: pointer;
  transition: all 0.15s;
  text-align: center;
  &:hover {
    border-color: $teal;
    color: $dark;
  }
  &.active {
    background: $dark;
    border-color: $dark;
    color: #fff;
  }
}

// ── Radio / checkbox ──────────────────────────────────────
.df-radio-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.df-radio {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: #3a3960;
  cursor: pointer;

  input[type="radio"] {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border: 1.5px solid #d1d5db;
    border-radius: 50%;
    background: #fff;
    cursor: pointer;
    flex-shrink: 0;
    outline: none;
    transition: border-color 0.15s, background 0.15s;
    position: relative;

    &:checked {
      border-color: $teal;
      background: $teal;
      &::after {
        content: "";
        position: absolute;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #fff;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }

    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: none;
    }
  }
}

// ── Footer buttons ────────────────────────────────────────
.df-clear-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: $grey;
  font-size: 13px;
  &:hover {
    color: $dark;
  }
}

.df-apply-btn {
  background: $teal;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 9px 22px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover {
    opacity: 0.85;
  }
}
</style>
